import { useEffect, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import { Oval } from "react-loader-spinner";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";
import { isWalkthroughRunning } from "src/Pages/KnowledgeBase/kbSession";

// Shared UI primitives for the WO upload modals (technical documents and
// jobsite photos/videos). Kept in one place so both flows render the same
// shell, dropzone, dropdown, and buttons.

export const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const ModalShell = ({ title, badge, onClose, children, footer, width = "w-[640px]" }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
    {/* off while a walkthrough runs: its tooltip sits outside this window,
        and a click on Next would otherwise close it (as createwomodal) */}
    <OutsideClickHandler onOutsideClick={onClose} disabled={isWalkthroughRunning()}>
      <div className={`${width} max-h-[90vh] flex flex-col bg-white rounded-[12px] overflow-hidden`} data-tour="modal-shell">
        <div className="px-6 py-5 border-b border-[#E5E8F3] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="font-OpenSansSemiBold text-[20px] leading-[28px] capitalize text-[#1A2332]">{title}</h2>
            {badge != null && (
              <span className="px-2 py-[2px] rounded-[8px] bg-[#EAF3FF] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#0058E5]">
                {badge}
              </span>
            )}
          </div>
          <button type="button" data-tour="modal-close" onClick={onClose} className="text-[#868DA6] cursor-pointer">
            <IoClose size={22} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto" data-tour="modal-body">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-[#E5E8F3] flex items-center justify-end gap-3">{footer}</div>
        )}
      </div>
    </OutsideClickHandler>
  </div>
);

/**
 * Right-side slide-in drawer. Same header/footer layout as ModalShell but
 * anchored to the right edge and full height. Used by the Edit Details flow.
 *
 * Stays mounted through the close transition so the panel can slide back out:
 * pass `shown` to drive enter/exit; the backdrop fades and the panel slides.
 */
export const DrawerShell = ({ shown, title, onClose, children, footer, width = "w-[420px]" }) => {
  // `mounted` keeps the drawer in the DOM during the exit transition; `visible`
  // toggles the enter/exit transform a frame after mount so the slide animates.
  const [mounted, setMounted] = useState(shown);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (shown) {
      setMounted(true);
      // Two frames: the first lets the browser paint the off-screen start state
      // (translate-x-full) after mount; the second flips to visible so the
      // transition actually runs. A single rAF can fire before the start state
      // is committed, so the browser skips the animation on later opens.
      let inner;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setVisible(true));
      });
      return () => {
        cancelAnimationFrame(outer);
        if (inner) cancelAnimationFrame(inner);
      };
    }
    // Closing: play the exit transition, then unmount after it finishes.
    setVisible(false);
    const timer = setTimeout(() => setMounted(false), 300);
    return () => clearTimeout(timer);
  }, [shown]);

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${
          visible ? "bg-opacity-30" : "bg-opacity-0"
        }`}
        onClick={onClose}
      />
      <div
        className={`relative ${width} max-w-[92vw] h-full flex flex-col bg-white shadow-[-8px_0px_24px_rgba(10,12.67,18,0.10)] transition-transform duration-300 ease-out ${
          visible ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="px-6 py-5 border-b border-[#E5E8F3] flex items-center justify-between">
          <h2 className="font-OpenSansSemiBold text-[20px] leading-[28px] capitalize text-[#1A2332]">{title}</h2>
          <button type="button" onClick={onClose} className="text-[#868DA6] cursor-pointer">
            <IoClose size={22} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-[#E5E8F3] flex items-center gap-3">{footer}</div>
        )}
      </div>
    </div>
  );
};

export const PrimaryButton = ({ children, onClick, disabled, loading, loadingLabel = "Uploading...", tour }) => (
  <button
    type="button"
    data-tour={tour}
    onClick={onClick}
    disabled={disabled || loading}
    className={`h-[40px] px-5 rounded-[8px] flex items-center justify-center gap-2 font-OpenSansSemiBold text-[14px] text-white ${
      loading ? "bg-[#98C1FF] cursor-not-allowed" : disabled ? "bg-[#0065FD] opacity-60 cursor-not-allowed" : "bg-[#0065FD] cursor-pointer"
    }`}
  >
    {loading ? (
      <>
        <Oval height="16" width="16" color="#FFFFFF" strokeWidth={8} secondaryColor="#C7DCFF" visible />
        {loadingLabel}
      </>
    ) : (
      children
    )}
  </button>
);

export const OutlineButton = ({ children, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`h-[44px] px-5 rounded-[8px] bg-white outline outline-1 -outline-offset-1 outline-[#C9CEE2] flex items-center justify-center font-OpenSansSemiBold text-[14px] leading-[20px] capitalize text-[#1A2332] ${
      disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
    }`}
  >
    {children}
  </button>
);

export const GhostButton = ({ children, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`h-[40px] px-3 rounded-[6px] flex items-center justify-center font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#7C829A] ${
      disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
    }`}
  >
    {children}
  </button>
);

export const FieldLabel = ({ children }) => (
  <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
    {children}
  </span>
);

export const ServiceTicketToggle = ({ checked, onChange, label, helper, disabled }) => (
  <div className="flex items-start gap-3">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      className={`relative inline-flex h-[24px] w-[44px] shrink-0 items-center rounded-[12px] p-[2px] transition-colors ${
        checked ? "bg-[#0065FD]" : "bg-[#EEF0F9]"
      } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-[20px] w-[20px] transform rounded-full bg-white shadow-[0px_1px_2px_rgba(10,12.67,18,0.10)] transition-transform ${
          checked ? "translate-x-[20px]" : "translate-x-0"
        }`}
      />
    </button>
    <div className="flex flex-col gap-[2px]">
      <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
        {label}
      </span>
      {helper && (
        <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A]">
          {helper}
        </span>
      )}
    </div>
  </div>
);

/**
 * Simple single-select dropdown matching the TagInput look. Used for Document
 * Type. `options` is a list of plain strings.
 */
export const SelectDropdown = ({ value, options, placeholder = "Select", onChange }) => {
  const [open, setOpen] = useState(false);

  return (
    <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
      <div className="relative w-full">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="w-full h-[40px] px-3 flex items-center justify-between gap-2 border border-[#E5E8F3] rounded-[6px] cursor-pointer"
        >
          <span
            className={`font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] ${
              value ? "text-[#1A2332]" : "text-[#868DA6]"
            }`}
          >
            {value || placeholder}
          </span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 20 20"
            fill="none"
            className={`text-[#868DA6] transition-transform ${open ? "rotate-180" : ""}`}
          >
            <path d="M5 7.5l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {open && (
          <ul className="absolute z-30 mt-1 w-full max-h-[220px] overflow-y-auto bg-white border border-[#E5E8F3] rounded-[8px] shadow-[0px_4px_12px_0px_rgba(10,12.67,18,0.10)]">
            {options.map((option) => (
              <li
                key={option}
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
                className={`px-3 py-2 cursor-pointer hover:bg-[#F8F9FE] text-[14px] leading-[20px] ${
                  option === value ? "font-OpenSansSemiBold text-[#0065FD]" : "font-OpenSansRegular text-[#1A2332]"
                }`}
              >
                {option}
              </li>
            ))}
          </ul>
        )}
      </div>
    </OutsideClickHandler>
  );
};

// `accept` only filters the file picker dialog, not drag-and-drop. This checks
// a dropped file against the comma-separated accept list. Each entry may be a
// MIME type, a wildcard MIME ("image/*"), or an extension (".pdf").
export const isAcceptedFile = (file, accept) => {
  if (!accept) return true;
  const allowed = accept.split(",").map((s) => s.trim().toLowerCase());
  const mime = (file.type || "").toLowerCase();
  const ext = `.${file.name?.split(".").pop()?.toLowerCase()}`;
  return allowed.some((rule) => {
    if (rule.endsWith("/*")) return mime.startsWith(rule.slice(0, -1));
    if (rule.startsWith(".")) return rule === ext;
    return rule === mime;
  });
};

// Inline-SVG dashed borders, matching the design's stroke-dasharray="6 4",
// stroke-width 2, rx 11. A url-encoded SVG rect gives an exact dash pattern that
// `border-dashed` can't reproduce. One per border color (idle / drag-active).
const dashedBorder = (stroke) =>
  `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='11' ry='11' stroke='${stroke}' stroke-width='2' stroke-dasharray='6 4'/%3e%3c/svg%3e")`;
const DASHED_IDLE = dashedBorder("%23C9CEE2");
const DASHED_ACTIVE = dashedBorder("%230065FD");

/**
 * Empty-state dropzone (screenshots 1-2): click to browse or drag & drop.
 * Swaps to a highlighted "Drop File To Upload" state while a drag is over it.
 *
 * `title` / `hint` set the idle label; `footer` is the supported-formats line.
 */
export const Dropzone = ({ accept, multiple, title = "Upload Document", hint = "Drag & Drop files here", footer, onFiles, onReject }) => {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const accepted = files.filter((file) => isAcceptedFile(file, accept));
    if (accepted.length < files.length) {
      onReject?.(`Unsupported file type. ${footer}`);
    }
    if (accepted.length) onFiles(multiple ? accepted : [accepted[0]]);
  };

  return (
    <div className="p-6">
      <div
        data-tour="upload-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragActive(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          handleFiles(e.dataTransfer.files);
        }}
        style={{ backgroundImage: dragActive ? DASHED_ACTIVE : DASHED_IDLE }}
        className={`w-full min-h-[278px] p-8 flex flex-col items-center justify-center rounded-[11px] cursor-pointer transition-colors ${
          dragActive ? "bg-[#EAF3FF]" : "bg-[#F8F9FE]"
        }`}
      >
        <div className="pb-4">
          <div className="w-[56px] h-[56px] rounded-full bg-white flex items-center justify-center shadow-[0px_1px_2px_rgba(0,0,0,0.05)]">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              {/* File-with-up-arrow glyph, matching the design's dropzone icon. */}
              <path
                d="M5.25 19V3a2.75 2.75 0 0 1 2.75-2.75h7c.199 0 .39.079.53.22l5 5c.141.14.22.331.22.53V19A2.75 2.75 0 0 1 17 21.75H8A2.75 2.75 0 0 1 5.25 19Z"
                stroke="#1A2332"
                strokeWidth="1.5"
              />
              <path d="M15 1V5A2 2 0 0 0 17 7H21" stroke="#1A2332" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M12 17v-6" stroke="#1A2332" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M9.5 12.5 12 10l2.5 2.5" stroke="#1A2332" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        {dragActive ? (
          <>
            <span className="pb-1 text-center font-OpenSansSemiBold text-[18px] leading-[26px] capitalize text-[#0065FD]">
              Drop File To Upload
            </span>
            <span className="text-center font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#5B8DEF]">
              Release mouse to begin upload
            </span>
          </>
        ) : (
          <>
            <span className="pb-1 text-center font-OpenSansSemiBold text-[18px] leading-[26px] capitalize text-[#1A2332]">
              {title}
            </span>
            <span className="pb-4 text-center font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#7C829A]">
              {hint}
            </span>
            <div className="pb-4 w-[200px] max-w-[200px] flex items-center">
              <span className="flex-1 h-px bg-[#E5E8F3]" />
              <span className="px-3 text-center font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] uppercase text-[#868DA6]">
                OR
              </span>
              <span className="flex-1 h-px bg-[#E5E8F3]" />
            </div>
            <span className="h-[40px] px-4 flex items-center justify-center rounded-[8px] bg-white shadow-[0px_1px_2px_rgba(16,24,40,0.05)] outline outline-1 -outline-offset-1 outline-[#C9CEE2] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#1A2332]">
              Choose File
            </span>
          </>
        )}
      </div>
      {footer && (
        <p className="pt-4 text-center font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#5F6477]">
          {footer}
        </p>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          if (e.target) e.target.value = null;
        }}
      />
    </div>
  );
};

export const FileTypeIcon = () => (
  <div className="w-[40px] h-[40px] shrink-0 rounded-[6.67px] bg-[#DBEAFE] flex items-center justify-center">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      {/* File with folded corner and text lines, matching the design's blue icon. */}
      <path
        d="M5 21V3a2 2 0 0 1 2-2h7c.27 0 .52.1.71.29l5 5c.19.19.29.44.29.71V21a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2Z"
        stroke="#0058E5"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M14 1v5a2 2 0 0 0 2 2h5" stroke="#0058E5" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 17h6M9 13h6M9 9h2" stroke="#0058E5" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  </div>
);

export const TrashIcon = () => (
  <svg width="13" height="15" viewBox="0 0 13 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.5 2.66667C12.7761 2.66667 13 2.89052 13 3.16667C13 3.44281 12.7761 3.66667 12.5 3.66667H0.5C0.223858 3.66667 0 3.44281 0 3.16667C0 2.89052 0.223858 2.66667 0.5 2.66667H12.5Z"
      fill="#7C829A"
    />
    <path
      d="M1.33333 12.5V3.16667C1.33333 2.89052 1.55719 2.66667 1.83333 2.66667C2.10948 2.66667 2.33333 2.89052 2.33333 3.16667V12.5C2.33333 12.6707 2.42369 12.8833 2.60352 13.0632C2.78334 13.243 2.99595 13.3333 3.16667 13.3333H9.83333C10.0041 13.3333 10.2167 13.243 10.3965 13.0632C10.5763 12.8833 10.6667 12.6707 10.6667 12.5V3.16667C10.6667 2.89052 10.8905 2.66667 11.1667 2.66667C11.4428 2.66667 11.6667 2.89052 11.6667 3.16667V12.5C11.6667 12.9959 11.4237 13.45 11.1035 13.7702C10.7833 14.0904 10.3293 14.3333 9.83333 14.3333H3.16667C2.67072 14.3333 2.21666 14.0904 1.89648 13.7702C1.57631 13.45 1.33333 12.9959 1.33333 12.5Z"
      fill="#7C829A"
    />
    <path
      d="M8.66667 3.16667V1.83333C8.66667 1.66262 8.57631 1.45001 8.39648 1.27018C8.21666 1.09036 8.00405 1 7.83333 1H5.16667C4.99595 1 4.78334 1.09036 4.60352 1.27018C4.42369 1.45001 4.33333 1.66262 4.33333 1.83333V3.16667C4.33333 3.44281 4.10948 3.66667 3.83333 3.66667C3.55719 3.66667 3.33333 3.44281 3.33333 3.16667V1.83333C3.33333 1.33738 3.57631 0.883324 3.89648 0.563151C4.21666 0.242978 4.67072 0 5.16667 0H7.83333C8.32928 0 8.78334 0.242978 9.10352 0.563151C9.42369 0.883324 9.66667 1.33738 9.66667 1.83333V3.16667C9.66667 3.44281 9.44281 3.66667 9.16667 3.66667C8.89052 3.66667 8.66667 3.44281 8.66667 3.16667Z"
      fill="#7C829A"
    />
    <path
      d="M4.66667 10.5V6.5C4.66667 6.22386 4.89052 6 5.16667 6C5.44281 6 5.66667 6.22386 5.66667 6.5V10.5C5.66667 10.7761 5.44281 11 5.16667 11C4.89052 11 4.66667 10.7761 4.66667 10.5Z"
      fill="#7C829A"
    />
    <path
      d="M7.33333 10.5V6.5C7.33333 6.22386 7.55719 6 7.83333 6C8.10948 6 8.33333 6.22386 8.33333 6.5V10.5C8.33333 10.7761 8.10948 11 7.83333 11C7.55719 11 7.33333 10.7761 7.33333 10.5Z"
      fill="#7C829A"
    />
  </svg>
);

export const ChevronToggle = ({ expanded }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    className={`text-[#868DA6] transition-transform ${expanded ? "rotate-180" : ""}`}
  >
    <path d="M5 7.5l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Compress oversized images before upload, matching the WoDetail attachment
// upload flow. Non-images (PDF, video) and small images pass through untouched.
export const compressIfNeeded = async (file, imageCompression) => {
  const isImage = file.type?.startsWith("image/");
  if (!isImage || file.size <= 5242880) return file;
  const blob = await imageCompression(file, {
    maxSizeMB: 5,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  });
  return new File([blob], file.name, { type: blob.type, lastModified: Date.now() });
};
